import { apiClient } from "./api-client";

export type ModelType = 'TTS' | 'ASR' | 'TEXT';
export type EvaluationMetric = 'MOS' | 'CMOS' | 'WER' | 'CER';
export type TestPhraseCategory = 'common' | 'technical' | 'literary' | 'conversational';

export interface ApiEndpointConfig {
    name: string;
    url: string;
    method: 'GET' | 'POST';
    authHeader?: string;
    authTokenEnvKey?: string;
    extraHeaders?: Record<string, string>;
    paramsMapping?: Record<string, string>;
}

export interface LomBenchModel {
    _id?: string;
    name: string;
    type: ModelType;
    version: string;
    langSupported: string[];
    apis: ApiEndpointConfig[];
    description?: string;
    createdAt?: Date;
}

export interface TestPhrase {
    _id?: string;
    lang: string;
    text: string;
    category: TestPhraseCategory;
    referenceAudioUrl?: string;
    hypothesisAudioUrl?: string;
    lastGeneratedAt?: Date;
    createdAt?: Date;
}

export interface ModelEvaluation {
    _id?: string;
    modelId: string;
    lang: string;
    type: ModelType;
    metric: EvaluationMetric;
    score: number;
    referenceText?: string;
    hypothesisText?: string;
    referenceAudioUrl?: string;
    hypothesisAudioUrl?: string;
    notes?: string;
    createdAt?: Date;
}

export interface CreateModelDto {
    name: string;
    type: ModelType;
    version: string;
    langSupported: string[];
    apis: ApiEndpointConfig[];
    description?: string;
}

export interface CreateTestPhraseDto {
    lang: string;
    text: string;
    category: TestPhraseCategory;
    referenceAudioUrl?: string;
}

export interface CreateEvaluationDto {
    modelId: string;
    lang: string;
    type: ModelType;
    metric: EvaluationMetric;
    score: number;
    details: {
        qualityRating: number;
        naturalness: number;
        pronunciation: number;
        comments: string;
    };
    referenceText?: string;
    hypothesisText?: string;
    referenceAudioUrl?: string;
    hypothesisAudioUrl?: string;
    notes?: string;
}

export interface TTSGenerateRequest {
    modelId: string;
    lang: string;
    text: string;
}

export interface TTSGenerateResponse {
    userId: string;
    lang: string;
    text: string;
    hypothesisAudioUrl: string;
    referenceAudioUrl?: string;
    modelId: string;
    type: 'TTS';
    createdAt: Date;
}

export interface ASRTranscribeRequest {
    modelId: string;
    lang: string;
    audioUrl?: string;
    audioUrlOrFile?: { url: string };
}

export interface ASRTranscribeResponse {
    userId: string;
    lang: string;
    hypothesisText: string;
    modelId: string;
    type: 'ASR';
    createdAt: Date;
}

export interface ModelAverages {
    modelId: string;
    lang?: string;
    averages: Record<string, number>;
    count: number;
}

export const lomBenchApi = {
  /**
   * Create a new model
   */
  createModel: async (model: CreateModelDto): Promise<LomBenchModel> => {
    const { data } = await apiClient.post<LomBenchModel>('/lombench/models', model);
    return data;
  },

    /**
     * Get models by type
     */
    findByType: async (type: ModelType): Promise<LomBenchModel[]> => {
        const { data } = await apiClient.get<LomBenchModel[]>(`/lombench/models/type/${type}`);
        return data;
    },

    /**
     * Add a test phrase
     */
    addTestPhrase: async (phrase: CreateTestPhraseDto): Promise<TestPhrase> => {
        const { data } = await apiClient.post<TestPhrase>('/lombench/phrases', phrase);
        return data;
    },

    /**
     * Get test phrases by language and category
     */
    getPhrasesByLang: async (
        lang: string,
        category?: TestPhraseCategory
    ): Promise<TestPhrase[]> => {
        const { data } = await apiClient.get<TestPhrase[]>('/lombench/phrases', {
            params: { lang, category },
        });
        return data;
    },

    /**
     * Generate TTS audio
     */
    ttsGenerate: async (request: TTSGenerateRequest): Promise<TTSGenerateResponse> => {
        const { data } = await apiClient.post<TTSGenerateResponse>('/lombench/tts/generate', request);
        return data;
    },

    /**
     * Transcribe audio with ASR
     */
    asrTranscribe: async (request: ASRTranscribeRequest): Promise<ASRTranscribeResponse> => {
        const { data } = await apiClient.post<ASRTranscribeResponse>('/lombench/asr/transcribe', request);
        return data;
    },

    /**
     * Transcribe an uploaded or recorded audio file with ASR
     */
    asrTranscribeFile: async (file: Blob, fileName: string, modelId: string, lang: string): Promise<ASRTranscribeResponse> => {
        const formData = new FormData();
        formData.append('file', file, fileName);
        formData.append('modelId', modelId);
        formData.append('lang', lang);
        const { data } = await apiClient.post<ASRTranscribeResponse>('/lombench/asr/file', formData);
        return data;
    },

    /**
     * Create an evaluation
     */
    createEvaluation: async (evaluation: CreateEvaluationDto): Promise<ModelEvaluation> => {
        const { data } = await apiClient.post<ModelEvaluation>('/lombench/evaluations', evaluation);
        return data;
    },
    /**
     * Get model averages
     */
    getModelAverages: async (modelId: string, lang?: string): Promise<ModelAverages> => {
        const { data } = await apiClient.get<ModelAverages>(`/lombench/evaluations/model/${modelId}`, {
            params: { lang },
        });
        return data;
    },
};
